import Image from 'next/image'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import styled from '@emotion/styled'
import NowrapDiv from 'components/common/NowrapDiv'
import ResponsiveDiv from 'components/common/ResponsiveDiv'
import { GtmAgent } from 'utils/hooks/analytics'
import { genPathByNavData } from 'utils/api'

const CardWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$hasLink',
})`
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05), 0px 0px 8px rgba(0, 0, 0, 0.05);
  ${({ $hasLink }) =>
    $hasLink
      ? `
    cursor: pointer;
    &:hover {
      img {
        transform: scale(1.1);
      }
    }
  `
      : ''}
`

const LabelChip = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  padding: 4px 8px;
  border-radius: 12px 0 12px 0;
  background-color: ${({ theme }) => theme.palette.secondary.minus2};
  color: white;
`

const ServiceItemCard = ({ item, onCardClick }) => {
  // 除了不送出點擊動作以外，其他類型都有動作前往其他頁面，因此只要判斷 page 即可
  const hasLink = !!item?.navData?.page && item.navData.page !== 'default'

  return (
    <CardWrapper $hasLink={hasLink} onClick={hasLink ? onCardClick : undefined}>
      {/* 圖片區 — 3:2 比例 */}
      <Box
        position="relative"
        overflow="hidden"
        sx={{ aspectRatio: '3/2', flexShrink: 0 }}
      >
        {item?.image?.url ? (
          <Image
            src={item.image.url}
            fill
            style={{
              objectFit: 'cover',
              transition: 'transform 0.3s ease',
            }}
            className={hasLink ? 'service-item-img' : undefined}
            alt={item.title || ''}
            quality={75}
          />
        ) : (
          <Box width="100%" height="100%" bgcolor="#d9d9d9" />
        )}
        {/* 標籤：疊加在圖片左上角 */}
        {!!item?.tag && <LabelChip>{item.tag}</LabelChip>}
      </Box>

      {/* 內容區 */}
      <Box
        px={4}
        pt={4}
        pb={5}
        display="flex"
        flexDirection="column"
        gap={4}
        flexGrow={1}
      >
        {/* 標題 */}
        <Typography variant="titleMedium" color="text.minus1">
          <NowrapDiv $lines={3}>{item?.title}</NowrapDiv>
        </Typography>

        {/* 說明 — 完整呈現 */}
        {!!item?.description && (
          <Typography
            variant="bodyMedium"
            color="text.minus2"
            sx={{ wordBreak: 'break-all' }}
          >
            {item.description}
          </Typography>
        )}
      </Box>
    </CardWrapper>
  )
}

const VenderServiceItems = ({ serviceItems, venderName, venderId }) => {
  const safeServiceItems = Array.isArray(serviceItems)
    ? serviceItems.filter((item) => item && typeof item === 'object')
    : []

  if (!safeServiceItems.length) return null

  return (
    <Box display="flex" flexDirection="column" gap={6} mb={18}>
      <ResponsiveDiv>
        <Typography variant="h5">服務項目</Typography>
      </ResponsiveDiv>
      <Grid
        container
        rowSpacing={{ xs: 4, md: 8 }}
        columnSpacing={{ xs: 4, lg: 6, xxl: 8 }}
      >
        {safeServiceItems.slice(0, 8).map((item, index) => (
          <Grid item xs={6} md={3} key={index} display="flex">
            <ServiceItemCard
              item={item}
              onCardClick={async () => {
                GtmAgent.directSendToGTM({
                  event: 'living_action',
                  living_event_name: 'vender_service_item',
                  living_click_name: item?.title,
                  living_click_location: '廠商首頁',
                  living_click_id: item?._id,
                  living_vender: venderName,
                  living_vender_id: venderId,
                })
                if (item?.navData) {
                  const result = await genPathByNavData(item.navData)
                  if (result?.path) {
                    const url =
                      !result.isExternalLink && /^\/service\//.test(result.path)
                        ? `${result.path}${
                            result.path.includes('?') ? '&' : '?'
                          }preselect_vender=${venderId}`
                        : result.path
                    window.open(url, '_blank')
                  }
                }
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

export default VenderServiceItems
